// Fixed assumptions
const MIN_COST = 13000; // (USD)
const MIN_HOURS = 43; // (hours)
const MIN_FLIGHTS_REF = 27; // (sessions, reference minimum path)
const hourlyRate = 300; // (USD/hr)
// Derived constant:
const HOURS_PER_SESSION = MIN_HOURS / MIN_FLIGHTS_REF;

// Lookup tables
export const trainingFrequencyOptions = {
	"1": { fpw: 1, w_freq: 0.0396, gapLoss: 0.343 },
	"2": { fpw: 2, w_freq: 0.0209, gapLoss: 0.138 },
	"3": { fpw: 3, w_freq: 0.0081, gapLoss: 0.0493 },
};

export const trainingEfficiencyOptions = {
	optimized: { e: 0.92, leakMult: 0.238, w_mult: 0.396 },
	typical: { e: 0.82, leakMult: 0.757, w_mult: 1.156 },
	"pushed back": { e: 0.7, leakMult: 1.414, w_mult: 0.807 },
};

export type TrainingEfficiencyOptions = keyof typeof trainingEfficiencyOptions;
export type TrainingFrequencyOptions = keyof typeof trainingFrequencyOptions;

type UseEstimateWasteCalculatorProps = {
	selectedTrainingEfficiencyLabel: TrainingEfficiencyOptions;
	selectedTrainingFrequencyLabel: TrainingFrequencyOptions;
};

const useEstimateWasteCalculator = ({
	selectedTrainingEfficiencyLabel,
	selectedTrainingFrequencyLabel,
}: UseEstimateWasteCalculatorProps) => {
	const selectedTrainingEfficiency =
		trainingEfficiencyOptions[selectedTrainingEfficiencyLabel];
	const selectedTrainingFrequency =
		trainingFrequencyOptions[selectedTrainingFrequencyLabel];
	const wasteGrowthRatePerSession =
		selectedTrainingEfficiency.w_mult * selectedTrainingFrequency.w_freq;
	const progressPerSession = HOURS_PER_SESSION * selectedTrainingEfficiency.e; // (principal paydown per session)
	const totalSessionsNeededToFinish = Math.ceil(MIN_HOURS / progressPerSession);
	const baseLeakPerSession =
		selectedTrainingFrequency.gapLoss +
		HOURS_PER_SESSION *
			(1 - selectedTrainingEfficiency.e) *
			selectedTrainingEfficiency.leakMult; // (hours of waste created each session)
	const wasteHours =
		wasteGrowthRatePerSession === 0
			? baseLeakPerSession * totalSessionsNeededToFinish
			: (baseLeakPerSession *
					((1 + wasteGrowthRatePerSession) ** totalSessionsNeededToFinish -
						1)) /
				wasteGrowthRatePerSession; // (closed form geometric series)
	// Waste cost (Avoidable cost)
	const wasteCostUSD = wasteHours * hourlyRate;
	// Total cost (Estimated total PPL cost)
	const totalCostUSD = MIN_COST + wasteCostUSD;
	// Total weeks (Estimated time to finish)
	const totalWeeks =
		totalSessionsNeededToFinish / selectedTrainingFrequency.fpw;
	// Avoidable delays
	const minWeeksAtSelectedPace =
		MIN_FLIGHTS_REF / selectedTrainingFrequency.fpw;
	const delaysWeeks = Math.max(0, totalWeeks - minWeeksAtSelectedPace);
	// Total hours
	const totalHours = MIN_HOURS + wasteHours;
	// Waste hours above minimum
	const wasteHoursAboveMin = Math.max(0, wasteHours);

	return {
		totalCost: Math.round(totalCostUSD),
		avoidableCost: Math.round(wasteCostUSD),
		totalWeeksToCompletion: Math.ceil(totalWeeks),
		avoidableWeeksToCompletion: Math.ceil(delaysWeeks),
		totalHoursToCompletion: Math.ceil(totalHours),
		avoidableHoursToCompletion: Math.ceil(wasteHoursAboveMin),
	};
};

export default useEstimateWasteCalculator;
